import { useEffect, useMemo } from "react";
import queryString from "query-string";

import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import { getCurrentPortalType } from "@vega-api/extended/auth/redirectUtils";
import { useMarkUserEmailAsActivated } from "@vega-api/generated/user-management-v1/user-management-v1";
import { ActionStatusMessage } from "@vega-core/components/Deprecated/ActionStatusMessage/ActionStatusMessage";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { NoticeType } from "@vega-core/components/Notice/Notice";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { m } from "@vega-core/paraglide/messages";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { trackEmailActivationViewed } from "@vega-core/utils/posthog/onboardingAnalytics";
import { OktaLoginButton } from "@vega-modules/components/OktaLoginButton/OktaLoginButton";

import { LoginLayout } from "./components/LoginLayout";

export const LPActivate = () => {
  const {
    global: { companyName },
  } = usePublicConfig();

  useEffect(() => {
    trackEmailActivationViewed();
  }, []);

  const { email = "" } = queryString.parse(location.search) as {
    email?: string;
  };

  const { isError, error, isSuccess, isLoading } = useMarkUserEmailAsActivated(
    { email },
    {
      query: {
        enabled: isTruthy(email),
      },
    },
  );

  const statusMessage = useMemo(() => {
    if (isFalsy(email)) return m.auth_oktaAccCreateEmailMissing();

    if (isError) {
      const { description } = getErrorMessage(error, {
        defaultMessage: m.auth_oktaAccCreateFail(),
      });

      return description;
    }

    if (isSuccess) return m.auth_oktaAccCreateSuccess();

    return "";
  }, [email, error, isError, isSuccess]);

  const statusVariant = useMemo(() => {
    if (isSuccess) return NoticeType.SUCCESS;

    if (isError || isFalsy(email)) return NoticeType.ERROR;

    return NoticeType.INFO;
  }, [isSuccess, isError, email]);

  if (isLoading) {
    return (
      <LoginLayout>
        <LoadingSpinner />
      </LoginLayout>
    );
  }

  return (
    <LoginLayout>
      <div className="flex flex-col h-full gap-4 align-center w-full text-text/auth/primary">
        <div className="flex flex-grow w-full flex-col justify-center p-12 align-center">
          <div className="max-w-lg">
            <h2 className="mt-6 whitespace-nowrap heading-5">
              {m.general_loginHeader({ companyName })}
            </h2>
            <div className="body-base-regular">{m.auth_oktaReSignIn()}</div>
            <div className="mt-4">
              <OktaLoginButton
                source={getCurrentPortalType()}
                variant="primary"
              />
            </div>
          </div>
        </div>
        <ActionStatusMessage message={statusMessage} type={statusVariant} />
      </div>
    </LoginLayout>
  );
};
